import { supabase } from './supabase';

// ============================================================
// HELPER: snake_case to camelCase conversion
// ============================================================
const toCamel = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamel);
  return Object.fromEntries(
    Object.entries(obj)?.map(([k, v]) => [
      k?.replace(/_([a-z])/g, (_, c) => c?.toUpperCase()),
      toCamel(v)
    ])
  );
};

const toSnake = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnake);
  return Object.fromEntries(
    Object.entries(obj)?.map(([k, v]) => [
      k?.replace(/([A-Z])/g, '_$1')?.toLowerCase(),
      v
    ])
  );
};

// ============================================================
// PAGES SERVICE
// ============================================================
export const pagesService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('pages')?.select('*, author:user_profiles(id, full_name, email)')?.order('updated_at', { ascending: false });

      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }
      if (filters?.pageType) {
        query = query?.eq('page_type', filters?.pageType);
      }
      if (filters?.search) {
        query = query?.ilike('title', `%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('pages')?.select('*, author:user_profiles(id, full_name, email), seo_metadata(*)')?.eq('id', id)?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async create(pageData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = toSnake({ ...pageData, authorId: user?.id });
      const { data, error } = await supabase?.from('pages')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async update(id, pageData) {
    try {
      const payload = toSnake(pageData);
      const { data, error } = await supabase?.from('pages')?.update(payload)?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('pages')?.delete()?.eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async getStats() {
    try {
      const { data, error } = await supabase?.from('pages')?.select('status');
      if (error) throw error;
      const stats = {
        total: data?.length || 0,
        published: data?.filter(p => p?.status === 'published')?.length || 0,
        draft: data?.filter(p => p?.status === 'draft')?.length || 0,
        archived: data?.filter(p => p?.status === 'archived')?.length || 0,
      };
      return { data: stats, error: null };
    } catch (error) {
      return { data: { total: 0, published: 0, draft: 0, archived: 0 }, error };
    }
  }
};

// ============================================================
// SEO METADATA SERVICE
// ============================================================
export const seoService = {
  async getByPageId(pageId) {
    try {
      const { data, error } = await supabase?.from('seo_metadata')?.select('*')?.eq('page_id', pageId)?.maybeSingle();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async upsert(pageId, seoData) {
    try {
      const payload = toSnake({ ...seoData, pageId });
      const { data, error } = await supabase?.from('seo_metadata')?.upsert(payload, { onConflict: 'page_id' })?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getAll() {
    try {
      const { data, error } = await supabase?.from('seo_metadata')?.select('*, page:pages(id, title, slug, status)')?.order('updated_at', { ascending: false });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  }
};

// ============================================================
// MENUS SERVICE
// ============================================================
export const menusService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('menus')?.select('*, menu_items(*, page:pages(id, title, slug))')?.order('created_at', { ascending: true });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async create(menuData) {
    try {
      const { data, error } = await supabase?.from('menus')?.insert(toSnake(menuData))?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async update(id, menuData) {
    try {
      const { data, error } = await supabase?.from('menus')?.update(toSnake(menuData))?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('menus')?.delete()?.eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};

// ============================================================
// SITE SETTINGS SERVICE
// ============================================================
export const settingsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('site_settings')?.select('*')?.order('category', { ascending: true });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async getByCategory(category) {
    try {
      const { data, error } = await supabase?.from('site_settings')?.select('*')?.eq('category', category);
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async update(key, value) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data, error } = await supabase?.from('site_settings')?.update({ setting_value: value, updated_by: user?.id, updated_at: new Date()?.toISOString() })?.eq('setting_key', key)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async upsert(settings) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = settings?.map(s => ({
        ...toSnake(s),
        updated_by: user?.id,
        updated_at: new Date()?.toISOString()
      }));
      const { data, error } = await supabase?.from('site_settings')?.upsert(payload, { onConflict: 'setting_key' })?.select();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// ============================================================
// MEDIA LIBRARY SERVICE (with Storage)
// ============================================================
export const mediaService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('media_library')?.select('*, uploader:user_profiles(id, full_name)')?.order('created_at', { ascending: false });

      if (filters?.mediaType) {
        query = query?.eq('media_type', filters?.mediaType);
      }
      if (filters?.search) {
        query = query?.ilike('original_name', `%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async upload(file, folder = 'uploads') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Build unique file path
      const timestamp = Date.now();
      const sanitizedName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${folder}/${timestamp}_${sanitizedName}`;

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase?.storage
        ?.from('cms-media')
        ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase?.storage
        ?.from('cms-media')
        ?.getPublicUrl(filePath);

      // Determine media type
      const mimeType = file?.type || '';
      let mediaType = 'document';
      if (mimeType?.startsWith('image/')) mediaType = 'image';
      else if (mimeType?.startsWith('video/')) mediaType = 'video';
      else if (mimeType?.startsWith('audio/')) mediaType = 'audio';

      // Save record to media_library table
      const payload = {
        filename: sanitizedName,
        original_name: file?.name,
        file_path: filePath,
        file_size: file?.size,
        mime_type: mimeType,
        media_type: mediaType,
        uploaded_by: user?.id
      };

      const { data, error } = await supabase?.from('media_library')?.insert(payload)?.select('*, uploader:user_profiles(id, full_name)')?.single();
      if (error) throw error;

      return { data: { ...toCamel(data), publicUrl: urlData?.publicUrl }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  getPublicUrl(filePath) {
    if (!filePath) return null;
    const { data } = supabase?.storage?.from('cms-media')?.getPublicUrl(filePath);
    return data?.publicUrl || null;
  },

  async delete(id, filePath) {
    try {
      // Delete from storage first
      if (filePath) {
        const { error: storageError } = await supabase?.storage
          ?.from('cms-media')
          ?.remove([filePath]);
        if (storageError) console.warn('Storage delete warning:', storageError?.message);
      }

      // Delete from database
      const { error } = await supabase?.from('media_library')?.delete()?.eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async create(mediaData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = toSnake({ ...mediaData, uploadedBy: user?.id });
      const { data, error } = await supabase?.from('media_library')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateMeta(id, updates) {
    try {
      const { data, error } = await supabase?.from('media_library')?.update(toSnake(updates))?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// ============================================================
// AUDIT LOGS SERVICE
// ============================================================
export const auditService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('audit_logs')?.select('*, user:user_profiles(id, full_name, email)')?.order('created_at', { ascending: false })?.limit(filters?.limit || 50);

      if (filters?.entityType) {
        query = query?.eq('entity_type', filters?.entityType);
      }
      if (filters?.userId) {
        query = query?.eq('user_id', filters?.userId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async log(action, entityType, entityId, oldValues = null, newValues = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { error } = await supabase?.from('audit_logs')?.insert({
        user_id: user?.id,
        action,
        entity_type: entityType,
        entity_id: entityId,
        old_values: oldValues,
        new_values: newValues
      });
      if (error) console.error('Audit log error:', error);
    } catch (error) {
      console.error('Audit log error:', error);
    }
  }
};

// ============================================================
// USER PROFILES SERVICE
// ============================================================
export const usersService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', id)?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async update(id, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(toSnake(updates))?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateRole(id, role) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ role })?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// ============================================================
// CONTENT BLOCKS SERVICE
// ============================================================
export const contentBlocksService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('content_blocks')?.select('*, creator:user_profiles(id, full_name)')?.order('created_at', { ascending: false });

      if (filters?.isReusable !== undefined) {
        query = query?.eq('is_reusable', filters?.isReusable);
      }
      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async create(blockData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = toSnake({ ...blockData, createdBy: user?.id });
      const { data, error } = await supabase?.from('content_blocks')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async update(id, blockData) {
    try {
      const { data, error } = await supabase?.from('content_blocks')?.update(toSnake(blockData))?.eq('id', id)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('content_blocks')?.delete()?.eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};

// ============================================================
// PAGE VERSIONS SERVICE
// ============================================================
export const pageVersionsService = {
  async getByPageId(pageId) {
    try {
      const { data, error } = await supabase?.from('page_versions')?.select('*, creator:user_profiles(id, full_name)')?.eq('page_id', pageId)?.order('version_number', { ascending: false });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async create(pageId, versionData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      // Get next version number
      const { count } = await supabase?.from('page_versions')?.select('*', { count: 'exact', head: true })?.eq('page_id', pageId);

      const payload = toSnake({
        ...versionData,
        pageId,
        versionNumber: (count || 0) + 1,
        createdBy: user?.id
      });
      const { data, error } = await supabase?.from('page_versions')?.insert(payload)?.select()?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// ============================================================
// TEMPLATES SERVICE
// ============================================================
export const templatesService = {
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('templates')?.select('*')?.order('downloads', { ascending: false });
      if (filters?.category && filters?.category !== 'all') {
        query = query?.eq('category', filters?.category);
      }
      if (filters?.isPro !== undefined) {
        query = query?.eq('is_pro', filters?.isPro);
      }
      if (filters?.isFeatured) {
        query = query?.eq('is_featured', true);
      }
      const { data, error } = await query;
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase?.from('templates')?.select('*')?.eq('id', id)?.single();
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async incrementDownloads(id) {
    try {
      const { data: current } = await supabase?.from('templates')?.select('downloads')?.eq('id', id)?.single();
      const { error } = await supabase?.from('templates')?.update({ downloads: (current?.downloads || 0) + 1 })?.eq('id', id);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};

// ============================================================
// ANALYTICS SERVICE
// ============================================================
export const analyticsService = {
  async trackEvent(eventType, pagePath, pageTitle, options = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const payload = {
        event_type: eventType,
        page_path: pagePath,
        page_title: pageTitle,
        session_id: options?.sessionId || sessionStorage?.getItem('analytics_session') || crypto?.randomUUID?.() || String(Date.now()),
        user_id: user?.id || null,
        device_type: /Mobi|Android/i?.test(navigator?.userAgent) ? 'mobile' : /Tablet|iPad/i?.test(navigator?.userAgent) ? 'tablet' : 'desktop',
        referrer: document?.referrer || 'direct',
        duration_seconds: options?.duration || null,
        metadata: options?.metadata || {}
      };
      if (!sessionStorage?.getItem('analytics_session')) {
        sessionStorage?.setItem('analytics_session', payload?.session_id);
      }
      const { error } = await supabase?.from('analytics_events')?.insert(payload);
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  async getMetrics(days = 7) {
    try {
      const since = new Date();
      since?.setDate(since?.getDate() - days);
      const { data, error } = await supabase
        ?.from('analytics_events')
        ?.select('*')
        ?.gte('created_at', since?.toISOString())
        ?.order('created_at', { ascending: true });
      if (error) throw error;
      return { data: toCamel(data), error: null };
    } catch (error) {
      return { data: [], error };
    }
  }
};
